import { useCallback, useSyncExternalStore } from 'react';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface Toast {
  id: string;
  message: string;
  type: ToastType;
  duration?: number;
}

// ── Global store ──────────────────────────────────────────────────────────────

let toasts: Toast[] = [];
const listeners = new Set<() => void>();

function notify() { listeners.forEach(fn => fn()); }

function addToast(message: string, type: ToastType = 'info', duration = 3200) {
  const id = Math.random().toString(36).slice(2);
  toasts = [...toasts, { id, message, type, duration }];
  notify();
  setTimeout(() => removeToast(id), duration);
  return id;
}

function removeToast(id: string) {
  toasts = toasts.filter(t => t.id !== id);
  notify();
}

// ── Public API (callable from anywhere, no React needed) ─────────────────────

export const toast = {
  success: (msg: string, duration?: number) => addToast(msg, 'success', duration),
  error:   (msg: string, duration?: number) => addToast(msg, 'error', duration ?? 5000),
  info:    (msg: string, duration?: number) => addToast(msg, 'info', duration),
  warning: (msg: string, duration?: number) => addToast(msg, 'warning', duration),
};

// ── Hook for the Toast display component ─────────────────────────────────────

export function useToastStore(): Toast[] {
  return useSyncExternalStore(
    cb => { listeners.add(cb); return () => listeners.delete(cb); },
    () => toasts,
  );
}

export function useDismissToast() {
  return useCallback((id: string) => removeToast(id), []);
}
